import React, { useState, useEffect, useRef, useCallback } from "react"
import { navigate } from "gatsby"
import { Search as SearchIcon, X, FileText, ArrowRight, Sparkles, Loader2 } from "lucide-react"
import { searchIndexUrl } from "@/lib/utils"

interface SearchDoc {
  id: number
  title: string
  description: string
  slug: string
  excerpt: string
  body: string
}

interface SearchResult {
  title: string
  slug: string
  excerpt: string
  description: string
  score: number
}

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
}

// On-device LLM integration via WebLLM / window.ai / Chrome built-in AI
async function getAISummary(query: string, results: SearchResult[]): Promise<string | null> {
  try {
    // Try Chrome's built-in AI (window.ai / Prompt API)
    const ai = (window as any).ai
    if (ai?.languageModel) {
      const session = await ai.languageModel.create({
        systemPrompt:
          "You are a helpful KB assistant for Hunto AI, a cybersecurity CTEM platform. Given a user search query and matching docs, provide a 1-2 sentence answer directing them to the right doc. Be concise.",
      })
      const context = results
        .slice(0, 5)
        .map((r) => `- ${r.title}: ${r.description || r.excerpt}`)
        .join("\n")
      const response = await session.prompt(
        `User searched for: "${query}"\n\nMatching docs:\n${context}\n\nProvide a brief helpful answer.`
      )
      session.destroy()
      return response
    }

    // Fallback: try window.ai (older spec)
    if ((window as any).model) {
      const session = await (window as any).model.createTextSession()
      const context = results
        .slice(0, 3)
        .map((r) => `${r.title}: ${r.description}`)
        .join("; ")
      const response = await session.prompt(
        `Briefly answer this search about Hunto AI cybersecurity platform: "${query}". Relevant docs: ${context}`
      )
      return response
    }
  } catch {
    // AI not available or errored — silently fall back
  }
  return null
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [docs, setDocs] = useState<SearchDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [aiSummary, setAiSummary] = useState<string | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [hasAI, setHasAI] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Check for on-device AI availability
  useEffect(() => {
    const checkAI = async () => {
      try {
        const ai = (window as any).ai
        if (ai?.languageModel) {
          const caps = await ai.languageModel.capabilities()
          if (caps.available === "readily" || caps.available === "after-download") {
            setHasAI(true)
          }
        }
      } catch {
        // no AI
      }
    }
    checkAI()
  }, [])

  // Load search index
  useEffect(() => {
    if (!isOpen) return
    if (docs.length > 0) {
      setLoading(false)
      return
    }
    fetch(searchIndexUrl())
      .then((res) => res.json())
      .then((data: SearchDoc[]) => {
        setDocs(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [isOpen, docs.length])

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery("")
      setResults([])
      setSelectedIndex(0)
      setAiSummary(null)
    }
  }, [isOpen])

  // Lock page scroll while the modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isOpen])

  // Search logic
  const search = useCallback(
    (q: string) => {
      if (!q.trim() || docs.length === 0) {
        setResults([])
        setAiSummary(null)
        return
      }

      const terms = q.toLowerCase().split(/\s+/).filter(Boolean)

      const scored = docs
        .map((doc) => {
          let score = 0
          const titleLower = doc.title.toLowerCase()
          const descLower = (doc.description || "").toLowerCase()
          const bodyLower = (doc.body || "").toLowerCase()
          const excerptLower = (doc.excerpt || "").toLowerCase()

          for (const term of terms) {
            if (titleLower === q.toLowerCase()) score += 25
            if (titleLower.startsWith(term)) score += 15
            if (titleLower.includes(term)) score += 10
            if (descLower.includes(term)) score += 5
            if (excerptLower.includes(term)) score += 3
            if (bodyLower.includes(term)) score += 1
          }

          return { ...doc, score }
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10)
        .map((r) => ({
          title: r.title,
          slug: r.slug,
          excerpt: r.excerpt || r.description,
          description: r.description,
          score: r.score,
        }))

      setResults(scored)
      setSelectedIndex(0)

      // Trigger AI summary if available
      if (hasAI && scored.length > 0) {
        setAiLoading(true)
        setAiSummary(null)
        getAISummary(q, scored).then((summary) => {
          setAiSummary(summary)
          setAiLoading(false)
        })
      }
    },
    [docs, hasAI]
  )

  useEffect(() => {
    const timer = setTimeout(() => search(query), 200)
    return () => clearTimeout(timer)
  }, [query, search])

  // Scroll selected into view
  useEffect(() => {
    if (resultsRef.current && selectedIndex >= 0) {
      const items = resultsRef.current.querySelectorAll("[data-result]")
      items[selectedIndex]?.scrollIntoView({ block: "nearest" })
    }
  }, [selectedIndex])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && results.length > 0 && selectedIndex >= 0) {
      e.preventDefault()
      navigate(results[selectedIndex].slug)
      onClose()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    }
  }

  const handleResultClick = (slug: string) => {
    navigate(slug)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-start justify-center pt-[15vh] px-4">
        <div
          className="w-full max-w-xl rounded-2xl border border-white/[0.18] bg-[#0d0d1a]/98 backdrop-blur-2xl shadow-2xl shadow-black/40 overflow-hidden animate-search-modal-in"
          role="dialog"
          aria-modal="true"
          aria-label="Search documentation"
        >
          {/* Input */}
          <div className="flex items-center gap-3 px-4 border-b border-white/[0.06]">
            <SearchIcon className="h-4 w-4 text-slate-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={loading ? "Loading…" : "Search documentation…"}
              disabled={loading}
              className="w-full bg-transparent py-3.5 text-sm text-white placeholder:text-slate-400 focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <kbd
              className="hidden sm:inline-flex items-center rounded border border-white/[0.1] bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-mono text-slate-300 shrink-0"
              onClick={onClose}
            >
              ESC
            </kbd>
          </div>

          {/* AI Summary */}
          {(aiLoading || aiSummary) && (
            <div className="px-4 py-3 border-b border-white/[0.06] bg-violet-500/[0.04]">
              <div className="flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-400 shrink-0 mt-0.5" />
                {aiLoading ? (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    AI is thinking…
                  </div>
                ) : (
                  <p className="text-xs text-slate-300 leading-relaxed">{aiSummary}</p>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          {query.trim() && (
            <div ref={resultsRef} className="max-h-[50vh] overflow-y-auto overscroll-contain">
              {results.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-slate-500">
                  No results for "<span className="text-slate-400">{query}</span>"
                </div>
              ) : (
                results.map((result, i) => (
                  <button
                    key={result.slug}
                    data-result
                    onClick={() => handleResultClick(result.slug)}
                    className={`flex items-center gap-3 w-full text-left px-4 py-2.5 transition-colors ${
                      i === selectedIndex
                        ? "bg-violet-500/10 text-white"
                        : "text-slate-300 hover:bg-white/[0.03]"
                    }`}
                  >
                    <FileText className="h-4 w-4 text-violet-400/60 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{result.title}</div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">
                        {result.description || result.excerpt}
                      </div>
                    </div>
                    <ArrowRight
                      className={`h-3 w-3 shrink-0 transition-opacity ${
                        i === selectedIndex ? "text-violet-400 opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                ))
              )}
            </div>
          )}

          {/* Footer hints */}
          {!query.trim() && !loading && (
            <div className="px-4 py-8 text-center">
              <p className="text-xs text-slate-300 mb-3">
                Search {docs.length} docs by title, description, or content
              </p>
              {hasAI && (
                <div className="inline-flex items-center gap-1.5 text-[10px] text-violet-300">
                  <Sparkles className="h-3 w-3" />
                  AI-powered answers available
                </div>
              )}
              <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-slate-300">
                <span><kbd className="px-1 py-0.5 rounded border border-white/[0.12] bg-white/[0.06] font-mono text-slate-200">↑↓</kbd> navigate</span>
                <span><kbd className="px-1 py-0.5 rounded border border-white/[0.12] bg-white/[0.06] font-mono text-slate-200">↵</kbd> open</span>
                <span><kbd className="px-1 py-0.5 rounded border border-white/[0.12] bg-white/[0.06] font-mono text-slate-200">esc</kbd> close</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SearchModal
